import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AsyncSelect from 'react-select/async';
import swal from 'sweetalert';
import { BASEURL } from '../../Config';

const LEAVE_TYPES = [
    { label: 'Leave', value: 'leave' },
    { label: 'Holiday', value: 'holiday' }
];

const SALES_MANAGER_PAGE_SIZE = 20;

const ApplyLeaveModal = ({
    show,
    onClose,
    onSuccess,
    salesManagerUUID = null, // If provided, uses this instead of showing dropdown
    salesManagerName = null  // Display name when salesManagerUUID is provided
}) => {
    const [leaveType, setLeaveType] = useState('leave');
    const [leaveDate, setLeaveDate] = useState('');
    const [leaveReason, setLeaveReason] = useState('');
    const [selectedSalesManager, setSelectedSalesManager] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Sales manager dropdown state for pagination
    const [salesManagerOptions, setSalesManagerOptions] = useState([]);
    const [salesManagerTotal, setSalesManagerTotal] = useState(0);
    const [salesManagerOffset, setSalesManagerOffset] = useState(0);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    useEffect(() => {
        if (show && !salesManagerUUID) {
            loadSalesManagers('', 0, true);
        }
    }, [show, salesManagerUUID]);

    const loadSalesManagers = async (search = '', offset = 0, reset = false) => {
        try {
            const res = await axios.get(`${BASEURL}/api/v1/salesManager?offset=${offset}&limit=${SALES_MANAGER_PAGE_SIZE}&fields=firstName,lastName,uuid${search ? `&search=${search}` : ''}`);
            if (res.status === 200) {
                const newOptions = res.data.salesManagers.map(sm => ({
                    label: `${sm.firstName} ${sm.lastName}`,
                    value: sm.uuid
                }));
                if (reset) {
                    setSalesManagerOptions(newOptions);
                } else {
                    setSalesManagerOptions(prev => [...prev, ...newOptions]);
                }
                setSalesManagerTotal(res.data.total);
                setSalesManagerOffset(offset + SALES_MANAGER_PAGE_SIZE);
            }
        } catch (err) {
            console.log('Error fetching sales managers', err);
        }
    };

    const loadMoreSalesManagers = () => {
        if (isLoadingMore || salesManagerOptions.length >= salesManagerTotal) return;
        setIsLoadingMore(true);
        loadSalesManagers('', salesManagerOffset, false).finally(() => setIsLoadingMore(false));
    };

    const handleSalesManagerSearch = async (inputValue) => {
        if (!inputValue) {
            loadSalesManagers('', 0, true);
            return salesManagerOptions;
        }
        try {
            const res = await axios.get(`${BASEURL}/api/v1/salesManager?offset=0&limit=${SALES_MANAGER_PAGE_SIZE}&fields=firstName,lastName,uuid&search=${inputValue}`);
            if (res.status === 200) {
                return res.data.salesManagers.map(sm => ({
                    label: `${sm.firstName} ${sm.lastName}`,
                    value: sm.uuid
                }));
            }
            return [];
        } catch (err) {
            console.log('Error searching sales managers', err);
            return [];
        }
    };

    const resetForm = () => {
        setLeaveType('leave');
        setLeaveDate('');
        setLeaveReason('');
        setSelectedSalesManager(null);
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const targetUUID = salesManagerUUID || selectedSalesManager?.value;

        if (!targetUUID) {
            swal('Error', 'Please select a sales manager', 'error');
            return;
        }
        if (!leaveDate) {
            swal('Error', 'Please select a date', 'error');
            return;
        }
        if (!leaveReason.trim()) {
            swal('Error', 'Please enter a reason', 'error');
            return;
        }

        setIsSubmitting(true);
        try {
            await axios.post(`${BASEURL}/api/v1/salesManagerAttendance/applyLeave`, {
                salesManagerUUID: targetUUID,
                date: leaveDate,
                reason: leaveReason.trim(),
                leaveType
            });
            swal('Success', `${leaveType === 'holiday' ? 'Holiday' : 'Leave'} applied successfully`, 'success');
            handleClose();
            if (onSuccess) {
                onSuccess();
            }
        } catch (err) {
            console.log(err.response?.data);
            swal('Error', err.response?.data?.message || err.response?.data || 'Failed to apply leave', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!show) return null;

    return (
        <div className="enhanced-modal-overlay" onClick={handleClose}>
            <div className="enhanced-modal modal-sm" onClick={(e) => e.stopPropagation()}>
                <div className="enhanced-modal-header">
                    <h5>Apply Leave / Holiday</h5>
                    <button onClick={handleClose}>&times;</button>
                </div>
                <form onSubmit={handleSubmit}>
                    <div className="enhanced-modal-body">
                        {!salesManagerUUID && (
                            <div className="form-group">
                                <label>Sales Manager <span className="text-danger">*</span></label>
                                <AsyncSelect
                                    value={selectedSalesManager}
                                    onChange={setSelectedSalesManager}
                                    loadOptions={handleSalesManagerSearch}
                                    defaultOptions={salesManagerOptions}
                                    onMenuScrollToBottom={loadMoreSalesManagers}
                                    isClearable
                                    placeholder="Search sales manager..."
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    isLoading={isLoadingMore}
                                    styles={{
                                        control: (base) => ({
                                            ...base,
                                            borderRadius: '8px',
                                            borderColor: '#ddd',
                                            minHeight: '42px',
                                            '&:hover': { borderColor: '#667eea' }
                                        }),
                                        menuPortal: (base) => ({ ...base, zIndex: 10001 })
                                    }}
                                />
                            </div>
                        )}
                        {salesManagerUUID && salesManagerName && (
                            <div className="form-group">
                                <label>Sales Manager</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={salesManagerName}
                                    disabled
                                />
                            </div>
                        )}
                        <div className="form-group">
                            <label>Type <span className="text-danger">*</span></label>
                            <select
                                value={leaveType}
                                onChange={(e) => setLeaveType(e.target.value)}
                                required
                            >
                                <option value="leave">Leave</option>
                                <option value="holiday">Holiday</option>
                            </select>
                        </div>
                        <div className="form-group">
                            <label>Date <span className="text-danger">*</span></label>
                            <input
                                type="date"
                                value={leaveDate}
                                onChange={(e) => setLeaveDate(e.target.value)}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label>Reason <span className="text-danger">*</span></label>
                            <textarea
                                value={leaveReason}
                                onChange={(e) => setLeaveReason(e.target.value)}
                                rows={3}
                                placeholder={leaveType === 'holiday' ? 'Enter holiday name or reason...' : 'Enter reason for leave...'}
                                required
                            />
                        </div>
                    </div>
                    <div className="enhanced-modal-footer">
                        <button type="button" className="btn-cancel" onClick={handleClose} disabled={isSubmitting}>
                            Cancel
                        </button>
                        <button type="submit" className="btn-save" disabled={isSubmitting}>
                            {isSubmitting ? 'Applying...' : 'Apply'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ApplyLeaveModal;
